import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createHorizontalRuleNode,
  $isHorizontalRuleNode,
  INSERT_HORIZONTAL_RULE_COMMAND,
} from '@lexical/react/LexicalHorizontalRuleNode';
import { $isListItemNode, $isListNode } from '@lexical/list';
import { mergeRegister, $insertNodeToNearestRoot } from '@lexical/utils';
import {
  $createNodeSelection,
  $createParagraphNode,
  $getNearestNodeFromDOMNode,
  $getSelection,
  $isElementNode,
  $isLineBreakNode,
  $isNodeSelection,
  $isParagraphNode,
  $isRangeSelection,
  $isRootNode,
  $isTextNode,
  $setSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_LOW,
  COMMAND_PRIORITY_NORMAL,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  KEY_ENTER_COMMAND,
} from 'lexical';
import { useEffect } from 'react';

function $isEmptyList(node) {
  return (
    $isListNode(node) &&
    node.getChildren().every(item => $isListItemNode(item) && item.getChildrenSize() === 0)
  );
}

function $isBlankParagraph(node) {
  return (
    $isParagraphNode(node) &&
    node.getTextContent().trim() === '' &&
    node.getChildren().every(child => $isTextNode(child) || $isLineBreakNode(child))
  );
}

// Lexical's insert helper splits the caret's block around the rule. From an
// empty paragraph that leaves an empty paragraph on each side; from a list it
// leaves an empty list below, so the caret lands in a fresh bullet. A blank
// paragraph is consumed, a rule ends a list, and a trailing empty block is
// kept only when there is nothing after the rule.
export function $insertHorizontalRule() {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return false;

  const rule = $createHorizontalRuleNode();
  const block = selection.anchor.getNode().getTopLevelElement();

  if ($isBlankParagraph(block)) {
    block.replace(rule);
  } else {
    $insertNodeToNearestRoot(rule);
  }

  const previous = rule.getPreviousSibling();
  if ($isEmptyList(previous)) previous.remove();

  let next = rule.getNextSibling();
  if ($isEmptyList(next)) {
    const paragraph = $createParagraphNode();
    next.replace(paragraph);
    next = paragraph;
  }
  const afterNext = next ? next.getNextSibling() : null;

  if ($isBlankParagraph(next) && afterNext) {
    next.remove();
    if ($isElementNode(afterNext)) afterNext.selectStart();
    else rule.selectNext();
  } else if (!next) {
    const paragraph = $createParagraphNode();
    rule.insertAfter(paragraph);
    paragraph.select();
  } else if ($isElementNode(next)) {
    next.selectStart();
  } else {
    rule.selectNext();
  }

  return true;
}

function $getRuleAtRootCaret() {
  const selection = $getSelection();
  if (!$isRangeSelection(selection) || !selection.isCollapsed()) return null;
  const { anchor } = selection;
  if (anchor.type !== 'element') return null;
  const root = anchor.getNode();
  if (!$isRootNode(root)) return null;
  return (
    [root.getChildAtIndex(anchor.offset), root.getChildAtIndex(anchor.offset - 1)].find(
      $isHorizontalRuleNode,
    ) || null
  );
}

// Arrowing past the text onto a rule that starts or ends the document parks the
// caret on the root itself, where typing opens a paragraph beside the rule.
export function $selectRuleAtRootCaret() {
  const rule = $getRuleAtRootCaret();
  if (!rule) return false;
  const selection = $createNodeSelection();
  selection.add(rule.getKey());
  $setSelection(selection);
  return true;
}

export function registerRootCaretGuard(editor) {
  return editor.registerUpdateListener(({ editorState }) => {
    if (editorState.read($getRuleAtRootCaret)) editor.update($selectRuleAtRootCaret);
  });
}

// Mousedown parks the caret on the root, so the guard above has selected the
// rule before Lexical's click handler toggles it, which would deselect on mouseup.
export function registerRuleClickGuard(editor) {
  return editor.registerCommand(
    CLICK_COMMAND,
    event => {
      if (event.shiftKey) return false;
      const node = $getNearestNodeFromDOMNode(event.target);
      if (!$isHorizontalRuleNode(node)) return false;
      const selection = $getSelection();
      return $isNodeSelection(selection) && selection.has(node.getKey());
    },
    COMMAND_PRIORITY_NORMAL,
  );
}

function $getSelectedRule() {
  const selection = $getSelection();
  if (!$isNodeSelection(selection)) return null;
  const nodes = selection.getNodes();
  return nodes.length === 1 && $isHorizontalRuleNode(nodes[0]) ? nodes[0] : null;
}

// Lexical's own deletion of a selected rule leaves the caret on the root beside
// the gap, the phantom line again. Land it in the neighbouring text instead.
export function $deleteSelectedRule(event, isBackward) {
  const rule = $getSelectedRule();
  if (!rule) return false;

  const parent = rule.getParentOrThrow();
  const previous = rule.getPreviousSibling();
  const next = rule.getNextSibling();
  event.preventDefault();
  rule.remove();

  const landing = (isBackward ? [previous, next] : [next, previous]).find($isElementNode);
  if (!landing) {
    const paragraph = $createParagraphNode();
    parent.append(paragraph);
    paragraph.select();
  } else if (landing === previous) {
    landing.selectEnd();
  } else {
    landing.selectStart();
  }
  return true;
}

// Lexical's Enter handlers only act on a range selection, so without this a
// selected rule swallows the key.
export function $insertParagraphAfterSelectedRule(event) {
  const rule = $getSelectedRule();
  if (!rule) return false;
  if (event) event.preventDefault();
  const paragraph = $createParagraphNode();
  rule.insertAfter(paragraph);
  paragraph.select();
  return true;
}

export default function HorizontalRulePlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        KEY_DELETE_COMMAND,
        event => $deleteSelectedRule(event, false),
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        KEY_BACKSPACE_COMMAND,
        event => $deleteSelectedRule(event, true),
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        KEY_ENTER_COMMAND,
        $insertParagraphAfterSelectedRule,
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        INSERT_HORIZONTAL_RULE_COMMAND,
        $insertHorizontalRule,
        COMMAND_PRIORITY_EDITOR,
      ),
      registerRootCaretGuard(editor),
      registerRuleClickGuard(editor),
    );
  }, [editor]);

  return null;
}
